/**
 * Invoice Print Templates Utility for TVFM POS System
 * Handles automatic layout selection based on transaction/billing type:
 * 1. SERVICE_WALLET: Dedicated layout for Service Wallet redemptions.
 * 2. CASH_WALLET: Dedicated layout for Cash/Price Wallet payments.
 * 3. NORMAL: Existing standard POS thermal invoice layout.
 */

import { getPaymentMethodBreakdowns } from './utils.js';
import { getInvoiceStoreGstNumber } from './storeGst.js';

/**
 * Automatically detects the billing type from completed bill / invoice data
 */
export const detectBillingType = (invoice) => {
    if (!invoice) return 'NORMAL';

    // 1. Check for Service Wallet redemption
    const hasServiceInItems = Array.isArray(invoice.items) && invoice.items.some(
        item => item?.isServiceWalletApplied || item?.customerWalletId || item?.serviceWalletName
    );
    const hasServiceInApplied = Array.isArray(invoice.appliedWallets) && invoice.appliedWallets.some(
        w => (w?.type === 'Service' || w?.walletType === 'Service')
    );
    const hasServiceWallet = hasServiceInItems || hasServiceInApplied;

    // 2. Check for Cash / Price Wallet payment
    const cashWalletDeduction = Number(invoice.cashWalletDeduction || 0);
    const hasCashInApplied = Array.isArray(invoice.appliedWallets) && invoice.appliedWallets.some(
        w => (w?.type === 'Cash' || w?.walletType === 'Cash')
    );
    const hasWalletInPaymentMode = Array.isArray(invoice.paymentMode)
        ? invoice.paymentMode.some(p => p?.method === 'WALLET' && Number(p?.amount || 0) > 0)
        : (invoice.paymentMode?.method === 'WALLET' && Number(invoice.paymentMode?.amount || 0) > 0);
    const hasCashWallet = cashWalletDeduction > 0 || hasCashInApplied || hasWalletInPaymentMode;

    if (hasServiceWallet && hasCashWallet) {
        return 'COMBINED_WALLET';
    } else if (hasServiceWallet) {
        return 'SERVICE_WALLET';
    } else if (hasCashWallet) {
        return 'CASH_WALLET';
    }

    return 'NORMAL';
};

/** Helper to extract item display name with variant name for products */
const getItemName = (item) => {
    if (item?.serviceId?.serviceName) return item.serviceId.serviceName;
    if (item?.subscriptionId?.subscriptionName) return item.subscriptionId.subscriptionName;

    const isProduct = item?.category === "product" || !!item?.productId;
    const baseTitle = item?.productId?.title || item?.productId?.productName || item?.productId?.name || item?.productName || (isProduct ? item?.name : null);
    const variant = (item?.productId?.variantName || item?.variantName || "").trim();

    if (baseTitle) {
        const cleanTitle = baseTitle.trim();
        if (variant && !cleanTitle.toLowerCase().includes(variant.toLowerCase())) {
            return `${cleanTitle} (${variant})`;
        }
        return cleanTitle;
    }

    return item?.serviceName || item?.name || item?.subscriptionName || "Item";
};

/** Helper to resolve store logo URL safely for thermal print windows */
export const resolveStoreLogo = (invoice, currentStore) => {
    const rawLogo =
        invoice?.storeId?.storeLogoImageObject ||
        invoice?.storeLogoImageObject ||
        invoice?.storeId?.logo ||
        invoice?.storeLogo ||
        currentStore?.storeLogoImageObject ||
        currentStore?.logo ||
        currentStore?.storeLogo ||
        "";

    if (!rawLogo) return "";

    let rawStr = "";
    if (typeof rawLogo === "string") {
        rawStr = rawLogo.trim();
    } else if (typeof rawLogo === "object") {
        rawStr = (rawLogo.url || rawLogo.uri || rawLogo.path || "").trim();
    }

    if (!rawStr) return "";

    // If it is already a complete URL or data URL
    if (rawStr.startsWith("http://") || rawStr.startsWith("https://") || rawStr.startsWith("data:")) {
        return rawStr;
    }

    let cleanUri = rawStr.replace(/^\/+/, "");
    // Remove 'public/' prefix if stored as 'public/uploads/...'
    if (cleanUri.startsWith("public/")) {
        cleanUri = cleanUri.replace(/^public\//, "");
    }

    const baseApi = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    if (!baseApi) {
        if (typeof window !== "undefined" && window.location) {
            // In dev on localhost, API is typically on 3001
            if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
                return `http://${window.location.hostname}:3001/${cleanUri}`;
            }
            return `${window.location.origin}/${cleanUri}`;
        }
        return `/${cleanUri}`;
    }

    // If cleanUri already starts with api/v1/, avoid duplicate api/v1
    if (cleanUri.startsWith("api/v1/") && baseApi.endsWith("/api/v1")) {
        const rootApi = baseApi.replace(/\/api\/v1$/, "");
        return `${rootApi}/${cleanUri}`;
    }

    return `${baseApi}/${cleanUri}`;
};

/** Helper to extract store details */
const getStoreDetails = (invoice, currentStore) => {
    return {
        storeName: invoice?.storeId?.storeName || currentStore?.storeName || "Store",
        storeAddress: invoice?.storeId?.address || currentStore?.address || "",
        storePhone: invoice?.storeId?.phoneNumber || currentStore?.phoneNumber || "",
        storeGst: getInvoiceStoreGstNumber(invoice, currentStore),
        storeLogo: resolveStoreLogo(invoice, currentStore)
    };
};

/** Helper to compute GST Summary map & HTML rows */
const computeGstSummary = (items, invoice) => {
    const gstSummaryMap = {};
    items.forEach(item => {
        // 🚫 EXCLUDE Service Wallet covered items from GST calculations
        if (item.isServiceWalletApplied || item.customerWalletId || item.serviceWalletName) {
            return;
        }

        const rate = Number(
            item.gstRate ||
            item.gstPercentage ||
            item.serviceId?.gstPercentage ||
            item.productId?.gstPercentage ||
            item.subscriptionId?.gstPercentage ||
            item.taxPercentage ||
            item.taxRate ||
            0
        );
        if (rate === 0) return;

        let gstAmt = Number(item.gstAmount || item.taxAmount || 0);
        if (gstAmt === 0 && rate > 0) {
            const price = Number(item.price || 0);
            const qty = Number(item.qty || 1);
            const total = price * qty;
            const isInclusive = invoice.gstType === "Inclusive" || item.category === "product" || !!item.productId;

            if (isInclusive) {
                gstAmt = total - (total / (1 + rate / 100));
            } else {
                gstAmt = (total * rate) / 100;
            }
        }

        const halfGst = gstAmt / 2;
        const isProduct = item.category === "product" || !!item.productId;

        if (!gstSummaryMap[rate]) {
            gstSummaryMap[rate] = { cgst: 0, sgst: 0, pcgst: 0, psgst: 0 };
        }

        if (isProduct) {
            gstSummaryMap[rate].pcgst += halfGst;
            gstSummaryMap[rate].psgst += halfGst;
        } else {
            gstSummaryMap[rate].cgst += halfGst;
            gstSummaryMap[rate].sgst += halfGst;
        }
    });

    const rows = Object.keys(gstSummaryMap).sort((a, b) => Number(a) - Number(b)).map(rate => {
        const data = gstSummaryMap[rate];
        return `
            <tr>
                <td>${rate}%</td>
                <td align="right">${data.cgst.toFixed(2)}</td>
                <td align="right">${data.sgst.toFixed(2)}</td>
                <td align="right">${data.pcgst.toFixed(2)}</td>
                <td align="right">${data.psgst.toFixed(2)}</td>
            </tr>
        `;
    }).join("");

    return rows || '<tr><td colspan="5" align="center">No GST (Service Wallet Covered)</td></tr>';
};

/** Standard Thermal CSS styles */
const getThermalCss = () => `
    @page {
        size: 80mm auto;
        margin: 0mm;
    }
    html, body {
        width: 80mm;
        margin: 0;
        padding: 0;
        font-family: Arial, sans-serif;
        background: #fff;
        color: #000;
    }
    body {
        display: flex;
        justify-content: center;
    }
    .box {
        width: 76mm;
        padding: 3mm;
        box-sizing: border-box;
        position: relative;
    }
    .center { text-align: center; }
    .logo-container {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto 8px auto;
        text-align: center;
        width: 100%;
    }
    .logo {
        max-width: 120px;
        max-height: 65px;
        width: auto;
        height: auto;
        object-fit: contain;
        display: block;
        margin: 0 auto;
    }
    .store-name { font-size: 15px; font-weight: 700; margin-top: 4px; line-height: 1.25; }
    .tax-title { font-size: 15px; font-weight: bold; margin-top: 3px; }
    .badge-title {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        border: 1px solid #000;
        padding: 2px 6px;
        display: inline-block;
        margin-top: 4px;
        border-radius: 4px;
    }
    .divider { border-top: 1px dashed #000; margin: 5px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { text-align: left; border-bottom: 1px dashed #000; padding: 3px 0; font-size: 11px; }
    td { padding: 2px 0; vertical-align: top; word-break: break-word; }
    .summary-table td { padding: 1px 0; }
    .bold { font-weight: bold; }
    .right { text-align: right; }
    .large { font-size: 13px; }
    .wallet-box {
        border: 1px solid #000;
        border-radius: 4px;
        padding: 4px 6px;
        margin: 5px 0;
        font-size: 10px;
    }
    .wallet-box-title {
        font-weight: bold;
        text-align: center;
        border-bottom: 1px dashed #000;
        padding-bottom: 2px;
        margin-bottom: 4px;
    }
    .footer {
        text-align: center;
        margin-top: 10px;
        font-size: 10px;
        font-weight: bold;
        line-height: 1.4;
    }
`;

/**
 * 1. SERVICE WALLET PRINT LAYOUT
 * Dedicated thermal receipt layout for Service Wallet redemptions matching Screenshot 2.
 * Shows Package Name, Service Name, Total Services, Used Services, Remaining Services, Validity, Expiry, etc.
 * Payment section: Customer Payable: ₹0, Amount Paid: ₹0, Paid Through: SERVICE WALLET.
 */
export const generateServiceWalletInvoiceHTML = (invoice, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(invoice, currentStore);
    const items = invoice.items || [];

    const custName = invoice.transactionType === "SELF" ? "Self" : (invoice.customerId?.name || invoice.customerId?.customerName || "Walk-in");
    const custPhone = invoice.customerId?.phoneNumber || "-";
    const billDateStr = `${new Date(invoice.invoiceDate || invoice.createdAt || Date.now()).toLocaleDateString('en-GB')}, ${new Date(invoice.createdAt || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;

    // Calculate totals across ALL items (Wallet covered + non-wallet services/products)
    let totalOriginalValue = 0;
    let totalWalletCoveredValue = 0;
    let totalCustomerPayableValue = 0;

    const generateParticularsRows = items.map(item => {
        const name = getItemName(item);
        const qty = Number(item.qty || 1);
        const origUnitPrice = Number(item.unitPriceOriginal || item.price || item.total || 0);
        const origTotal = origUnitPrice * qty;

        const isWalletApplied = !!(item.isServiceWalletApplied || item.customerWalletId || item.serviceWalletName);
        const walletCovered = isWalletApplied ? origTotal : 0;
        const customerPayableForItem = isWalletApplied ? 0 : Math.max(0, Number(item.total || origTotal) - Number(item.manualDiscount || item.discount || 0));

        totalOriginalValue += origTotal;
        totalWalletCoveredValue += walletCovered;
        totalCustomerPayableValue += customerPayableForItem;

        return `
            <tr>
                <td style="padding: 3px 0;">
                    <strong>${name}</strong>
                    ${isWalletApplied ? `<br/><small style="font-size:9px; color:#2563eb; font-weight:bold;">[WALLET APPLIED]</small>` : ''}
                </td>
                <td align="center">${qty}</td>
                <td align="right">₹${origUnitPrice.toFixed(2)}</td>
                <td align="right" style="font-weight:bold;">
                    ${isWalletApplied ? `<span style="color:#16a34a;">WALLET (₹0)</span>` : `₹${customerPayableForItem.toFixed(2)}`}
                </td>
            </tr>
        `;
    }).join("");

    // Calculate Discounts & Taxes
    const itemWiseDiscount = items.reduce((sum, item) => sum + Number(item.manualDiscount || item.itemDiscount || item.discount || 0), 0);
    const membershipDiscount = Number(invoice.membershipDiscount || 0);
    const overallDiscount = Number(invoice.overallDiscount || invoice.discount || 0);
    const couponDiscount = Number(invoice.couponDiscount || 0);
    const totalDiscount = membershipDiscount + overallDiscount + couponDiscount + itemWiseDiscount;

    // GST ONLY from non-wallet items
    const nonWalletItems = items.filter(i => !(i.isServiceWalletApplied || i.customerWalletId || i.serviceWalletName));
    const totalGST = nonWalletItems.reduce((sum, item) => sum + Number(item.gstAmount || 0), 0);

    const roundOff = Number(invoice.roundOff || 0);
    const miscCharges = Number(invoice.miscellaneousCharges || 0);

    const walletDeduction = totalWalletCoveredValue > 0
        ? totalWalletCoveredValue
        : Number(invoice.serviceWalletCoveredAmount || 0);

    const customerPayable = Math.max(0, totalCustomerPayableValue);

    // Payment details formatting
    const isMixedBill = customerPayable > 0;
    const paymentString = getPaymentMethodBreakdowns(invoice).join(", ");

    // Extract Service Wallet Details
    const serviceWalletEntries = [];
    items.forEach(item => {
        if (item.isServiceWalletApplied || item.customerWalletId || item.serviceWalletName) {
            const matchedWallet = Array.isArray(invoice.appliedWallets)
                ? invoice.appliedWallets.find(w => String(w.walletId) === String(item.customerWalletId) || w.type === 'Service' || w.walletType === 'Service')
                : null;

            const itemSvcId = String(item.serviceId?._id || item.serviceId || '');
            const itemSvcName = (getItemName(item) || '').toLowerCase().trim();

            let matchedSvcEntry = null;
            if (matchedWallet && Array.isArray(matchedWallet.services)) {
                matchedSvcEntry = matchedWallet.services.find(s => {
                    const sId = String(s.serviceId?._id || s.serviceId || '');
                    const sName = (s.serviceId?.serviceName || s.serviceName || '').toLowerCase().trim();
                    return (sId && itemSvcId && sId === itemSvcId) || (sName && itemSvcName && (sName === itemSvcName || sName.includes(itemSvcName) || itemSvcName.includes(sName)));
                });
            }

            const totalUsagesForSvc = matchedSvcEntry?.totalCount || 
                (matchedSvcEntry?.baseCount ? (matchedSvcEntry.baseCount + (matchedSvcEntry.additionalCount || 0)) : null) || 
                matchedWallet?.totalCount || 
                matchedWallet?.totalServiceCount || 
                (matchedWallet?.baseCount ? (matchedWallet.baseCount + (matchedWallet.extraCount || matchedWallet.additionalCount || 0)) : null) || 
                item.totalCount || 
                "-";
            
            let remUsagesForSvc = "-";
            if (matchedWallet?.remainingCount !== undefined && matchedWallet?.remainingCount !== null) {
                remUsagesForSvc = matchedWallet.remainingCount;
            } else if (item.remainingCount !== undefined && item.remainingCount !== null) {
                remUsagesForSvc = item.remainingCount;
            } else if (matchedSvcEntry?.remainingCount !== undefined && matchedSvcEntry?.remainingCount !== null) {
                remUsagesForSvc = Math.max(0, matchedSvcEntry.remainingCount - (item.qty || 1));
            } else if (totalUsagesForSvc !== "-") {
                remUsagesForSvc = Math.max(0, Number(totalUsagesForSvc) - (item.qty || 1));
            }

            serviceWalletEntries.push({
                serviceName: getItemName(item) || matchedSvcEntry?.serviceId?.serviceName || matchedWallet?.serviceName || "Service",
                walletName: item.serviceWalletName || matchedWallet?.walletName || "Service Wallet",
                usageRedeemed: item.qty || 1,
                remainingUsage: remUsagesForSvc,
                purchasedServices: totalUsagesForSvc,
                validityDays: matchedWallet?.validityDays || matchedWallet?.walletPackageId?.validityDays || null,
                rechargeDate: matchedWallet?.createdAt ? new Date(matchedWallet.createdAt).toLocaleDateString('en-GB') : "-",
                expiryDate: item.walletExpiry ? new Date(item.walletExpiry).toLocaleDateString('en-GB') : (matchedWallet?.expiryDate ? new Date(matchedWallet.expiryDate).toLocaleDateString('en-GB') : "-"),
                packageAmount: Number(matchedWallet?.price || matchedWallet?.totalAmount || matchedWallet?.walletPackageId?.price || 0)
            });
        }
    });

    if (serviceWalletEntries.length === 0 && Array.isArray(invoice.appliedWallets)) {
        invoice.appliedWallets.filter(w => w.type === 'Service' || w.walletType === 'Service').forEach(w => {
            const bC = Number(w.baseCount || 0);
            const eC = Number(w.extraCount || w.additionalCount || 0);
            const sumC = bC + eC;
            const totUsages = Math.max(Number(w.totalCount || 0), Number(w.totalServiceCount || 0), sumC) || "-";
            const remUsages = w.remainingCount ?? (totUsages !== "-" ? Math.max(0, Number(totUsages) - (w.usageCount || 1)) : "-");
            serviceWalletEntries.push({
                serviceName: w.serviceName || "Service Package",
                walletName: w.walletName || "Service Wallet",
                usageRedeemed: w.usageCount || 1,
                remainingUsage: remUsages,
                purchasedServices: totUsages,
                validityDays: w.validityDays || w.walletPackageId?.validityDays || null,
                rechargeDate: w.createdAt ? new Date(w.createdAt).toLocaleDateString('en-GB') : "-",
                expiryDate: w.expiryDate ? new Date(w.expiryDate).toLocaleDateString('en-GB') : "-",
                packageAmount: Number(w.price || w.totalAmount || w.walletPackageId?.price || 0)
            });
        });
    }

    const serviceWalletRowsHtml = serviceWalletEntries.map(e => `
        <tr><td>Package Name</td><td align="right"><strong>${e.walletName}</strong></td></tr>
        <tr><td>Service Name</td><td align="right"><strong>${e.serviceName}</strong></td></tr>
        <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
        ${e.purchasedServices !== "-" ? `<tr><td>Total Services Purchased</td><td align="right">${e.purchasedServices} Usages</td></tr>` : ""}
        <tr><td>Services Used (This Bill)</td><td align="right"><strong>${e.usageRedeemed} Usage${Number(e.usageRedeemed) > 1 ? 's' : ''}</strong></td></tr>
        <tr class="bold large"><td>Remaining Services</td><td align="right" style="color:#16a34a;"><strong>${e.remainingUsage !== "-" ? `${e.remainingUsage} Usages` : "-"}</strong></td></tr>
        ${e.rechargeDate !== "-" ? `<tr><td>Recharge Date</td><td align="right">${e.rechargeDate}</td></tr>` : ""}
        ${e.validityDays ? `<tr><td>Wallet Validity</td><td align="right">${e.validityDays} Days</td></tr>` : ""}
        ${e.expiryDate !== "-" ? `<tr><td>Wallet Expiry Date</td><td align="right"><strong>${e.expiryDate}</strong></td></tr>` : ""}
        ${e.packageAmount > 0 ? `<tr><td>Package Purchase Amount</td><td align="right">₹${e.packageAmount.toFixed(2)}</td></tr>` : ""}
    `).join("<tr><td colspan='2'><div style='border-top:1px dotted #ccc; margin:4px 0;'></div></td></tr>");

    const gstSummaryRows = computeGstSummary(items, invoice);

    return `
<html>
<head>
    <title>Service Wallet Invoice - ${invoice.invoiceNo}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Tax Invoice</div>
            <div class="badge-title">${isMixedBill ? 'SERVICE WALLET & MIXED BILLING' : 'SERVICE WALLET REDEMPTION'}</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="40%">Customer Name</td><td>: <strong>${custName}</strong></td></tr>
            ${custPhone !== "-" ? `<tr><td>Phone Number</td><td>: ${custPhone}</td></tr>` : ""}
            <tr><td>Bill Date</td><td>: ${billDateStr}</td></tr>
            <tr><td>Invoice No.</td><td>: ${invoice.invoiceNo}</td></tr>
        </table>

        <div class="divider"></div>

        <table>
            <thead>
                <tr>
                    <th width="45%">PARTICULARS</th>
                    <th align="center">QTY</th>
                    <th align="right">RATE</th>
                    <th align="right">PAYABLE</th>
                </tr>
            </thead>
            <tbody>
                ${generateParticularsRows}
            </tbody>
        </table>

        <div class="divider"></div>

        <table class="summary-table">
            <tr><td>ORIGINAL BILL VALUE</td><td align="right">₹${totalOriginalValue.toFixed(2)}</td></tr>
            ${miscCharges > 0 ? `<tr><td>MISC CHARGES</td><td align="right">₹${miscCharges.toFixed(2)}</td></tr>` : ""}
            ${totalDiscount > 0 ? `<tr><td>DISCOUNTS</td><td align="right">-₹${totalDiscount.toFixed(2)}</td></tr>` : ""}
            ${totalGST > 0 ? `<tr><td>GST AMOUNT</td><td align="right">₹${totalGST.toFixed(2)}</td></tr>` : ""}
            ${roundOff !== 0 ? `<tr><td>ROUND OFF</td><td align="right">₹${roundOff.toFixed(2)}</td></tr>` : ""}
            ${walletDeduction > 0 ? `<tr class="bold"><td>SERVICE WALLET COVERED</td><td align="right" style="color:#2563eb;">-₹${walletDeduction.toFixed(2)}</td></tr>` : ""}
            <tr class="bold large"><td>CUSTOMER PAYABLE</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>
            <tr class="bold large"><td>AMOUNT PAID</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>
        </table>

        ${serviceWalletEntries.length > 0 ? `
        <div class="divider"></div>
        <div class="wallet-box">
            <div class="wallet-box-title">SERVICE WALLET REDEMPTION SUMMARY</div>
            <table>
                ${serviceWalletRowsHtml}
            </table>
        </div>
        ` : ""}

        <div class="divider"></div>

        <div class="bold">GST Tax Summary :</div>
        <table style="margin-top:5px;">
            <thead>
                <tr>
                    <th>GST</th>
                    <th align="right">CGST</th>
                    <th align="right">SGST</th>
                    <th align="right">PCGST</th>
                    <th align="right">PSGST</th>
                </tr>
            </thead>
            <tbody>
                ${gstSummaryRows}
            </tbody>
        </table>

        <div class="divider" style="margin-top:10px;"></div>

        <table>
            <tr class="bold"><td>PAID THROUGH</td><td align="right">${paymentString}</td></tr>
            <tr><td>CUSTOMER PAYABLE</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>
            <tr><td>AMOUNT PAID</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
            ${isMixedBill ? 'SERVICE WALLET & MIXED BILLING INVOICE.' : 'SERVICE COVERED UNDER SERVICE WALLET PACKAGE.'}
        </div>
    </div>
</body>
</html>
    `;
};

/**
 * 2. CASH / PRICE WALLET PRINT LAYOUT
 * Dedicated thermal receipt layout for Cash/Price Wallet payments matching Screenshot 2.
 * Shows Wallet Name, Recharge Date, Recharge Amount, Total Wallet Credit, Bill Amount, Amount Used, Remaining Balance, Validity, etc.
 */
export const generateCashWalletInvoiceHTML = (invoice, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(invoice, currentStore);
    const items = invoice.items || [];

    const custName = invoice.transactionType === "SELF" ? "Self" : (invoice.customerId?.name || invoice.customerId?.customerName || "Walk-in");
    const custPhone = invoice.customerId?.phoneNumber || "-";
    const billDateStr = `${new Date(invoice.invoiceDate || invoice.createdAt || Date.now()).toLocaleDateString('en-GB')}, ${new Date(invoice.createdAt || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    const billAmount = Number(invoice.grandTotal || invoice.finalAmount || 0);

    // Extract Cash Wallet Details
    let cashWalletUsed = Number(invoice.cashWalletDeduction || 0);
    let walletName = "Cash / Price Wallet";
    let remainingWalletBalance = null;
    let currentBalance = null;
    let totalWalletCredit = null;
    let walletCreditAmount = null;
    let rechargeAmount = null;
    let rechargeDate = null;
    let walletValidity = null;
    let rechargeValidity = null;
    let walletExpiry = null;

    if (Array.isArray(invoice.appliedWallets)) {
        const cashWalletEntry = invoice.appliedWallets.find(w => w.type === 'Cash' || w.walletType === 'Cash');
        if (cashWalletEntry) {
            if (cashWalletUsed === 0) cashWalletUsed = Number(cashWalletEntry.amountUsed || 0);
            if (cashWalletEntry.walletName) walletName = cashWalletEntry.walletName;
            if (cashWalletEntry.remainingBalance !== undefined && cashWalletEntry.remainingBalance !== null) {
                remainingWalletBalance = Number(cashWalletEntry.remainingBalance);
            }
            if (cashWalletEntry.currentBalance !== undefined && cashWalletEntry.currentBalance !== null) {
                currentBalance = Number(cashWalletEntry.currentBalance);
            }
            if (cashWalletEntry.totalCredited !== undefined && cashWalletEntry.totalCredited !== null) {
                totalWalletCredit = Number(cashWalletEntry.totalCredited);
            }
            if (cashWalletEntry.walletCredit !== undefined && cashWalletEntry.walletCredit !== null) {
                walletCreditAmount = Number(cashWalletEntry.walletCredit);
            } else if (cashWalletEntry.creditedAmount !== undefined && cashWalletEntry.creditedAmount !== null) {
                walletCreditAmount = Number(cashWalletEntry.creditedAmount);
            }
            if (cashWalletEntry.amountPaid !== undefined && cashWalletEntry.amountPaid !== null) {
                rechargeAmount = Number(cashWalletEntry.amountPaid);
            } else if (cashWalletEntry.price !== undefined && cashWalletEntry.price !== null) {
                rechargeAmount = Number(cashWalletEntry.price);
            } else if (cashWalletEntry.rechargeAmount !== undefined && cashWalletEntry.rechargeAmount !== null) {
                rechargeAmount = Number(cashWalletEntry.rechargeAmount);
            }
            if (cashWalletEntry.rechargeDate || cashWalletEntry.createdAt) {
                rechargeDate = new Date(cashWalletEntry.rechargeDate || cashWalletEntry.createdAt).toLocaleDateString('en-GB');
            }
            if (cashWalletEntry.validityDays) walletValidity = cashWalletEntry.validityDays;
            if (cashWalletEntry.rechargeValidityDays) rechargeValidity = cashWalletEntry.rechargeValidityDays;
            if (cashWalletEntry.expiryDate) walletExpiry = new Date(cashWalletEntry.expiryDate).toLocaleDateString('en-GB');
        }
    }

    if (cashWalletUsed === 0 && Array.isArray(invoice.paymentMode)) {
        const walletPmt = invoice.paymentMode.find(p => p.method === "WALLET");
        if (walletPmt) cashWalletUsed = Number(walletPmt.amount || 0);
    }

    if (cashWalletUsed === 0) {
        cashWalletUsed = billAmount;
    }

    if (invoice.cashWalletPreviousBalance !== undefined && invoice.cashWalletPreviousBalance !== null) {
        currentBalance = Number(invoice.cashWalletPreviousBalance);
    }

    if (invoice.cashWalletRemainingBalance !== undefined && invoice.cashWalletRemainingBalance !== null) {
        remainingWalletBalance = Number(invoice.cashWalletRemainingBalance);
    } else if (invoice.remainingWalletBalance !== undefined && invoice.remainingWalletBalance !== null) {
        remainingWalletBalance = Number(invoice.remainingWalletBalance);
    }

    if (currentBalance !== null && currentBalance !== undefined && currentBalance > 0) {
        if (remainingWalletBalance === null || remainingWalletBalance === undefined || (remainingWalletBalance === 0 && currentBalance > cashWalletUsed)) {
            remainingWalletBalance = Math.max(0, currentBalance - cashWalletUsed);
        }
    } else if (remainingWalletBalance !== null && remainingWalletBalance !== undefined) {
        currentBalance = remainingWalletBalance + cashWalletUsed;
    } else if (totalWalletCredit !== null && totalWalletCredit > 0) {
        currentBalance = totalWalletCredit;
        remainingWalletBalance = Math.max(0, currentBalance - cashWalletUsed);
    }

    if (currentBalance === null && remainingWalletBalance !== null) {
        currentBalance = remainingWalletBalance + cashWalletUsed;
    }

    const additionalAmountPaid = Math.max(0, billAmount - cashWalletUsed);
    let additionalPaymentMethod = "";
    if (additionalAmountPaid > 0 && Array.isArray(invoice.paymentMode)) {
        const nonWalletPmts = invoice.paymentMode.filter(p => p.method !== "WALLET" && Number(p.amount) > 0);
        if (nonWalletPmts.length > 0) {
            additionalPaymentMethod = nonWalletPmts.map(p => `${p.method} (₹${Number(p.amount).toFixed(2)})`).join(", ");
        }
    }

    return `
<html>
<head>
    <title>Cash Wallet Receipt - ${invoice.invoiceNo}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Wallet Receipt</div>
            <div class="badge-title">CASH / PRICE WALLET DETAILS</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="40%">Customer Name</td><td>: <strong>${custName}</strong></td></tr>
            ${custPhone !== "-" ? `<tr><td>Phone Number</td><td>: ${custPhone}</td></tr>` : ""}
            <tr><td>Print Date</td><td>: ${billDateStr}</td></tr>
            <tr><td>Invoice No.</td><td>: ${invoice.invoiceNo}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="wallet-box">
            <div class="wallet-box-title">CASH WALLET BREAKDOWN</div>
            <table>
                <tr><td>Wallet Name</td><td align="right"><strong>${walletName}</strong></td></tr>
                ${rechargeDate ? `<tr><td>Recharge Date</td><td align="right">${rechargeDate}</td></tr>` : ""}
                ${rechargeAmount !== null ? `<tr><td>Recharge / Purchase Amount</td><td align="right">₹${rechargeAmount.toFixed(2)}</td></tr>` : ""}
                ${walletCreditAmount !== null ? `<tr><td>Wallet Credit Amount</td><td align="right">₹${walletCreditAmount.toFixed(2)}</td></tr>` : ""}
                ${totalWalletCredit !== null ? `<tr class="bold"><td>Total Credited Amount</td><td align="right">₹${totalWalletCredit.toFixed(2)}</td></tr>` : ""}
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:4px 0;"></div></td></tr>
                ${currentBalance !== null ? `<tr><td>Previous Wallet Balance</td><td align="right">₹${currentBalance.toFixed(2)}</td></tr>` : ""}
                <tr><td>Original Bill Amount</td><td align="right">₹${billAmount.toFixed(2)}</td></tr>
                <tr class="bold"><td>Amount Used (This Bill)</td><td align="right" style="color:#dc2626;">-₹${cashWalletUsed.toFixed(2)}</td></tr>
                <tr class="bold large"><td>Remaining Wallet Balance</td><td align="right" style="color:#16a34a;">₹${Number(remainingWalletBalance ?? 0).toFixed(2)}</td></tr>
                ${additionalAmountPaid > 0 ? `<tr class="bold"><td>Additional Amount Paid</td><td align="right">₹${additionalAmountPaid.toFixed(2)}${additionalPaymentMethod ? ` (${additionalPaymentMethod})` : ''}</td></tr>` : ""}
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:4px 0;"></div></td></tr>
                ${walletValidity ? `<tr><td>Wallet Validity</td><td align="right">${walletValidity} Days</td></tr>` : ""}
                ${rechargeValidity && rechargeValidity !== walletValidity ? `<tr><td>Recharge Validity</td><td align="right">${rechargeValidity} Days</td></tr>` : ""}
                ${walletExpiry ? `<tr><td>Wallet Expiry Date</td><td align="right"><strong>${walletExpiry}</strong></td></tr>` : ""}
            </table>
        </div>

        ${items.length > 0 ? `
        <div class="divider"></div>
        <div style="font-weight:bold; font-size:10px; margin-bottom:3px;">BILLED ITEMS:</div>
        <table>
            <thead>
                <tr>
                    <th width="50%">ITEM</th>
                    <th align="center">QTY</th>
                    <th align="right">AMOUNT</th>
                </tr>
            </thead>
            <tbody>
                ${items.map(it => `
                    <tr>
                        <td style="padding:1px 0;">${getItemName(it)}</td>
                        <td align="center">${it.qty || 1}</td>
                        <td align="right">₹${Number(it.total || it.price || 0).toFixed(2)}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
        ` : ''}

        <div class="divider"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
            CASH WALLET SUMMARY RECEIPT.
        </div>
    </div>
</body>
</html>
    `;
};

/**
 * 3. NORMAL BILLING PRINT LAYOUT
 * Preserves the exact existing standard thermal layout pixel-for-pixel.
 */
export const generateNormalInvoiceHTML = (invoice, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(invoice, currentStore);
    const items = invoice.items || [];

    const grandTotal = Number(invoice.grandTotal || invoice.finalAmount || 0);

    const itemWiseDiscount = items.reduce((sum, item) => sum + Number(item.manualDiscount || item.itemDiscount || item.discount || 0), 0);
    const membershipDiscount = Number(invoice.membershipDiscount || 0);
    const overallDiscount = Number(invoice.overallDiscount || invoice.discount || 0);
    const couponDiscount = Number(invoice.couponDiscount || 0);
    const totalDiscount = membershipDiscount + overallDiscount + couponDiscount + itemWiseDiscount;

    const totalGST = Number(invoice.totalGST || 0);
    const roundOff = Number(invoice.roundOff || 0);
    const billAmount = grandTotal;
    const netAmount = Math.max(0, billAmount - totalGST - roundOff);
    const miscCharges = Number(invoice.miscellaneousCharges || 0);
    const basicSales = netAmount + totalDiscount - miscCharges;

    const generateRows = (list) => {
        return list.map((item) => {
            const name = getItemName(item);
            const qty = Number(item.qty || 1);
            const itRate = Number(
                item.gstRate || item.gstPercentage || item.serviceId?.gstPercentage || item.productId?.gstPercentage || item.subscriptionId?.gstPercentage || item.taxPercentage || item.taxRate || 0
            );

            let amount = Number(item.taxableAmount || 0);
            if (amount === 0) {
                const price = Number(item.price || 0);
                const total = price * qty;
                const isInclusive = invoice.gstType === "Inclusive" || item.category === "product" || !!item.productId;
                if (isInclusive && itRate > 0) {
                    amount = total / (1 + itRate / 100);
                } else {
                    amount = total;
                }
            }
            const rate = amount / qty;
            return `
                <tr>
                    <td style="padding: 2px 0;">${name}</td>
                    <td align="center">${qty}</td>
                    <td align="right">${rate.toFixed(2)}</td>
                    <td align="right">${amount.toFixed(2)}</td>
                </tr>
            `;
        }).join("");
    };

    const tenderAmount = Array.isArray(invoice.paymentMode)
        ? invoice.paymentMode.reduce((sum, p) => sum + Number(p.amount || 0), 0)
        : Number(invoice.paymentMode?.amount || grandTotal);
    const changeAmount = Math.max(0, tenderAmount - billAmount);

    const paymentString = getPaymentMethodBreakdowns(invoice).join(", ");

    const gstSummaryRows = computeGstSummary(items, invoice);

    return `
<html>
<head>
    <title>Tax Invoice - ${invoice.invoiceNo}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Tax Invoice</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="35%">CustomerName</td><td>: ${invoice.transactionType === "SELF" ? "Self" : (invoice.customerId?.name || "Walk-in")}</td></tr>
            ${invoice.transactionType !== "SELF" ? `
            <tr><td>CustomerPhone</td><td>: ${invoice.customerId?.phoneNumber || "-"}</td></tr>
            ` : ""}
            <tr><td>BillDate</td><td>: ${new Date(invoice.invoiceDate || invoice.createdAt || Date.now()).toLocaleDateString('en-GB')}, ${new Date(invoice.createdAt || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</td></tr>
            <tr><td>InvoiceNo.</td><td>: ${invoice.invoiceNo}</td></tr>
        </table>

        <div class="divider"></div>

        <table>
            <thead>
                <tr>
                    <th width="45%">PARTICULARS</th>
                    <th align="center">QTY</th>
                    <th align="right">RATE</th>
                    <th align="right">AMOUNT</th>
                </tr>
            </thead>
            <tbody>
                ${generateRows(items)}
            </tbody>
        </table>

        <div class="divider"></div>

        <table class="summary-table">
            <tr><td>BASIC SALES</td><td align="right">${basicSales.toFixed(2)}</td></tr>
            ${miscCharges > 0 ? `<tr><td>MISC CHARGES${invoice.reason ? ` (${invoice.reason})` : ''}</td><td align="right">${miscCharges.toFixed(2)}</td></tr>` : ""}
            ${itemWiseDiscount > 0 ? `<tr><td>ITEM DISCOUNT</td><td align="right">-${itemWiseDiscount.toFixed(2)}</td></tr>` : ""}
            ${membershipDiscount > 0 ? `<tr><td>MEM.DISCOUNT</td><td align="right">-${membershipDiscount.toFixed(2)}</td></tr>` : ""}
            ${couponDiscount > 0 ? `<tr><td>COUPON DISCOUNT</td><td align="right">-${couponDiscount.toFixed(2)}</td></tr>` : ""}
            ${overallDiscount > 0 ? `<tr><td>OVERALL DISCOUNT</td><td align="right">-${overallDiscount.toFixed(2)}</td></tr>` : ""}
            <tr class="bold"><td>NET AMOUNT</td><td align="right">${netAmount.toFixed(2)}</td></tr>
            <tr><td>GST AMOUNT</td><td align="right">${totalGST.toFixed(2)}</td></tr>
            <tr><td>ROUND OFF</td><td align="right">${roundOff.toFixed(2)}</td></tr>
            <tr class="bold large"><td>BILL AMOUNT</td><td align="right">${billAmount.toFixed(2)}</td></tr>
            <tr><td>TENDER AMOUNT</td><td align="right">${tenderAmount.toFixed(2)}</td></tr>
            <tr><td>CHANGE AMOUNT</td><td align="right">${changeAmount.toFixed(2)}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="bold">GST Tax Summary :</div>
        <table style="margin-top:5px;">
            <thead>
                <tr>
                    <th>GST</th>
                    <th align="right">CGST</th>
                    <th align="right">SGST</th>
                    <th align="right">PCGST</th>
                    <th align="right">PSGST</th>
                </tr>
            </thead>
            <tbody>
                ${gstSummaryRows}
            </tbody>
        </table>

        <div class="divider" style="margin-top:15px;"></div>

        <table>
            <tr><td>PAID THROUGH</td><td align="right">${paymentString || "Cash"}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
        </div>
    </div>
</body>
</html>
    `;
};

/**
 * MASTER ROUTER FUNCTION
 * Automatically selects the appropriate print layout based on the transaction billing type.
 */
export const generateCombinedWalletInvoiceHTML = (invoice, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(invoice, currentStore);
    const items = invoice.items || [];

    const custName = invoice.transactionType === "SELF" ? "Self" : (invoice.customerId?.name || invoice.customerId?.customerName || "Walk-in");
    const custPhone = invoice.customerId?.phoneNumber || "-";
    const billDateStr = `${new Date(invoice.invoiceDate || invoice.createdAt || Date.now()).toLocaleDateString('en-GB')}, ${new Date(invoice.createdAt || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    const invoiceNo = invoice.invoiceNo;

    // Calculate Service Wallet stuff
    let totalOriginalValue = 0;
    let totalWalletCoveredValue = 0;
    let totalCustomerPayableValue = 0;

    const generateParticularsRows = items.map(item => {
        const name = getItemName(item);
        const qty = Number(item.qty || 1);
        const origUnitPrice = Number(item.unitPriceOriginal || item.price || item.total || 0);
        const origTotal = origUnitPrice * qty;

        const isWalletApplied = !!(item.isServiceWalletApplied || item.customerWalletId || item.serviceWalletName);
        const walletCovered = isWalletApplied ? origTotal : 0;
        const customerPayableForItem = isWalletApplied ? 0 : Math.max(0, Number(item.total || origTotal) - Number(item.manualDiscount || item.discount || 0));

        totalOriginalValue += origTotal;
        totalWalletCoveredValue += walletCovered;
        totalCustomerPayableValue += customerPayableForItem;

        return `
            <tr>
                <td style="padding: 3px 0;">
                    <strong>${name}</strong>
                    ${isWalletApplied ? `<br/><small style="font-size:9px; color:#2563eb; font-weight:bold;">[SVC WALLET]</small>` : ''}
                </td>
                <td align="center">${qty}</td>
                <td align="right">₹${origUnitPrice.toFixed(2)}</td>
                <td align="right" style="font-weight:bold;">
                    ${isWalletApplied ? `<span style="color:#16a34a;">₹0</span>` : `₹${customerPayableForItem.toFixed(2)}`}
                </td>
            </tr>
        `;
    }).join("");

    const itemWiseDiscount = items.reduce((sum, item) => sum + Number(item.manualDiscount || item.itemDiscount || item.discount || 0), 0);
    const membershipDiscount = Number(invoice.membershipDiscount || 0);
    const overallDiscount = Number(invoice.overallDiscount || invoice.discount || 0);
    const couponDiscount = Number(invoice.couponDiscount || 0);
    const totalDiscount = membershipDiscount + overallDiscount + couponDiscount + itemWiseDiscount;

    const nonWalletItems = items.filter(i => !(i.isServiceWalletApplied || i.customerWalletId || i.serviceWalletName));
    const totalGST = nonWalletItems.reduce((sum, item) => sum + Number(item.gstAmount || 0), 0);

    const roundOff = Number(invoice.roundOff || 0);
    const miscCharges = Number(invoice.miscellaneousCharges || 0);

    const serviceWalletDeduction = totalWalletCoveredValue > 0 ? totalWalletCoveredValue : Number(invoice.serviceWalletCoveredAmount || 0);

    // Calculate Cash Wallet stuff
    let cashWalletUsed = Number(invoice.cashWalletDeduction || 0);
    let cashWalletName = "Cash Wallet";
    let remainingCashWalletBalance = null;
    let currentCashBalance = null;
    let cashWalletExpiry = null;

    if (Array.isArray(invoice.appliedWallets)) {
        const cashWalletEntry = invoice.appliedWallets.find(w => w.type === 'Cash' || w.walletType === 'Cash');
        if (cashWalletEntry) {
            if (cashWalletUsed === 0) cashWalletUsed = Number(cashWalletEntry.amountUsed || 0);
            if (cashWalletEntry.walletName) cashWalletName = cashWalletEntry.walletName;
            
            if (cashWalletEntry.remainingBalance !== undefined && cashWalletEntry.remainingBalance !== null) {
                remainingCashWalletBalance = Number(cashWalletEntry.remainingBalance);
            }
            if (cashWalletEntry.currentBalance !== undefined && cashWalletEntry.currentBalance !== null) {
                currentCashBalance = Number(cashWalletEntry.currentBalance);
            }
            if (cashWalletEntry.expiryDate) cashWalletExpiry = new Date(cashWalletEntry.expiryDate).toLocaleDateString('en-GB');
        }
    }

    if (cashWalletUsed === 0 && Array.isArray(invoice.paymentMode)) {
        const walletPmt = invoice.paymentMode.find(p => p.method === "WALLET");
        if (walletPmt) cashWalletUsed = Number(walletPmt.amount || 0);
    }
    if (cashWalletUsed === 0) cashWalletUsed = Math.max(0, invoice.grandTotal || 0);

    if (invoice.cashWalletPreviousBalance !== undefined && invoice.cashWalletPreviousBalance !== null) {
        currentCashBalance = Number(invoice.cashWalletPreviousBalance);
    }
    if (invoice.cashWalletRemainingBalance !== undefined && invoice.cashWalletRemainingBalance !== null) {
        remainingCashWalletBalance = Number(invoice.cashWalletRemainingBalance);
    } else if (invoice.remainingWalletBalance !== undefined && invoice.remainingWalletBalance !== null) {
        remainingCashWalletBalance = Number(invoice.remainingWalletBalance);
    }

    if (currentCashBalance !== null && currentCashBalance !== undefined && currentCashBalance > 0) {
        if (remainingCashWalletBalance === null || remainingCashWalletBalance === undefined || (remainingCashWalletBalance === 0 && currentCashBalance > cashWalletUsed)) {
            remainingCashWalletBalance = Math.max(0, currentCashBalance - cashWalletUsed);
        }
    } else if (remainingCashWalletBalance !== null && remainingCashWalletBalance !== undefined) {
        currentCashBalance = remainingCashWalletBalance + cashWalletUsed;
    }
    
    if (currentCashBalance === null && remainingCashWalletBalance !== null) {
        currentCashBalance = remainingCashWalletBalance + cashWalletUsed;
    }

    const billAmount = Number(invoice.grandTotal || invoice.finalAmount || 0);
    const customerPayable = Math.max(0, totalCustomerPayableValue - cashWalletUsed);
    const totalAmountPaid = billAmount;
    
    const paymentString = getPaymentMethodBreakdowns(invoice).join(", ");

    // Extract Service Wallet Entries
    const serviceWalletEntries = [];
    items.forEach(item => {
        if (item.isServiceWalletApplied || item.customerWalletId || item.serviceWalletName) {
            const matchedWallet = Array.isArray(invoice.appliedWallets) ? invoice.appliedWallets.find(w => String(w.walletId) === String(item.customerWalletId) || w.type === 'Service' || w.walletType === 'Service') : null;
            const itemSvcId = String(item.serviceId?._id || item.serviceId || '');
            const itemSvcName = (getItemName(item) || '').toLowerCase().trim();
            let matchedSvcEntry = null;
            if (matchedWallet && Array.isArray(matchedWallet.services)) {
                matchedSvcEntry = matchedWallet.services.find(s => {
                    const sId = String(s.serviceId?._id || s.serviceId || '');
                    const sName = (s.serviceId?.serviceName || s.serviceName || '').toLowerCase().trim();
                    return (sId && itemSvcId && sId === itemSvcId) || (sName && itemSvcName && (sName === itemSvcName || sName.includes(itemSvcName) || itemSvcName.includes(sName)));
                });
            }
            const totalUsagesForSvc = matchedSvcEntry?.totalCount || (matchedSvcEntry?.baseCount ? (matchedSvcEntry.baseCount + (matchedSvcEntry.additionalCount || 0)) : null) || matchedWallet?.totalCount || matchedWallet?.totalServiceCount || (matchedWallet?.baseCount ? (matchedWallet.baseCount + (matchedWallet.extraCount || matchedWallet.additionalCount || 0)) : null) || item.totalCount || "-";
            let remUsagesForSvc = "-";
            if (matchedWallet?.remainingCount !== undefined && matchedWallet?.remainingCount !== null) { remUsagesForSvc = matchedWallet.remainingCount; } else if (item.remainingCount !== undefined && item.remainingCount !== null) { remUsagesForSvc = item.remainingCount; } else if (matchedSvcEntry?.remainingCount !== undefined && matchedSvcEntry?.remainingCount !== null) { remUsagesForSvc = Math.max(0, matchedSvcEntry.remainingCount - (item.qty || 1)); } else if (totalUsagesForSvc !== "-") { remUsagesForSvc = Math.max(0, Number(totalUsagesForSvc) - (item.qty || 1)); }

            serviceWalletEntries.push({
                serviceName: getItemName(item) || matchedSvcEntry?.serviceId?.serviceName || matchedWallet?.serviceName || "Service",
                walletName: item.serviceWalletName || matchedWallet?.walletName || "Service Wallet",
                usageRedeemed: item.qty || 1,
                remainingUsage: remUsagesForSvc,
                validityDays: matchedWallet?.validityDays || matchedWallet?.walletPackageId?.validityDays || null,
                expiryDate: item.walletExpiry ? new Date(item.walletExpiry).toLocaleDateString('en-GB') : (matchedWallet?.expiryDate ? new Date(matchedWallet.expiryDate).toLocaleDateString('en-GB') : "-")
            });
        }
    });

    if (serviceWalletEntries.length === 0 && Array.isArray(invoice.appliedWallets)) {
        invoice.appliedWallets.filter(w => w.type === 'Service' || w.walletType === 'Service').forEach(w => {
            const totUsages = Math.max(Number(w.totalCount || 0), Number(w.totalServiceCount || 0), Number(w.baseCount || 0) + Number(w.extraCount || w.additionalCount || 0)) || "-";
            const remUsages = w.remainingCount ?? (totUsages !== "-" ? Math.max(0, Number(totUsages) - (w.usageCount || 1)) : "-");
            serviceWalletEntries.push({
                serviceName: w.serviceName || "Service Package",
                walletName: w.walletName || "Service Wallet",
                usageRedeemed: w.usageCount || 1,
                remainingUsage: remUsages,
                validityDays: w.validityDays || w.walletPackageId?.validityDays || null,
                expiryDate: w.expiryDate ? new Date(w.expiryDate).toLocaleDateString('en-GB') : "-"
            });
        });
    }

    const serviceWalletRowsHtml = serviceWalletEntries.map(e => `
        <tr><td>Package Name</td><td align="right"><strong>${e.walletName}</strong></td></tr>
        <tr><td>Service Name</td><td align="right"><strong>${e.serviceName}</strong></td></tr>
        <tr><td>Services Used (This Bill)</td><td align="right"><strong>${e.usageRedeemed} Usage${Number(e.usageRedeemed) > 1 ? 's' : ''}</strong></td></tr>
        <tr class="bold"><td>Remaining Services</td><td align="right" style="color:#16a34a;"><strong>${e.remainingUsage !== "-" ? `${e.remainingUsage} Usages` : "-"}</strong></td></tr>
        ${e.validityDays ? `<tr><td>Wallet Validity</td><td align="right">${e.validityDays} Days</td></tr>` : ""}
        ${e.expiryDate !== "-" ? `<tr><td>Wallet Expiry Date</td><td align="right"><strong>${e.expiryDate}</strong></td></tr>` : ""}
    `).join("<tr><td colspan='2'><div style='border-top:1px dotted #ccc; margin:4px 0;'></div></td></tr>");

    const gstSummaryRows = computeGstSummary(items, invoice);

    return `
<html>
<head>
    <title>Consolidated Wallet Invoice - ${invoiceNo}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Tax Invoice</div>
            <div class="badge-title">CONSOLIDATED WALLET REDEMPTION</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="40%">Customer Name</td><td>: <strong>${custName}</strong></td></tr>
            ${custPhone !== "-" ? `<tr><td>Phone Number</td><td>: ${custPhone}</td></tr>` : ""}
            <tr><td>Bill Date</td><td>: ${billDateStr}</td></tr>
            <tr><td>Invoice No.</td><td>: ${invoiceNo}</td></tr>
        </table>

        <div class="divider"></div>

        <table>
            <thead>
                <tr>
                    <th width="45%">PARTICULARS</th>
                    <th align="center">QTY</th>
                    <th align="right">RATE</th>
                    <th align="right">PAYABLE</th>
                </tr>
            </thead>
            <tbody>
                ${generateParticularsRows}
            </tbody>
        </table>

        <div class="divider"></div>

        <table class="summary-table">
            <tr><td>ORIGINAL BILL AMOUNT</td><td align="right">₹${billAmount.toFixed(2)}</td></tr>
            ${miscCharges > 0 ? `<tr><td>MISC CHARGES</td><td align="right">₹${miscCharges.toFixed(2)}</td></tr>` : ""}
            ${totalDiscount > 0 ? `<tr><td>DISCOUNTS</td><td align="right">-₹${totalDiscount.toFixed(2)}</td></tr>` : ""}
            ${totalGST > 0 ? `<tr><td>GST AMOUNT</td><td align="right">₹${totalGST.toFixed(2)}</td></tr>` : ""}
            ${roundOff !== 0 ? `<tr><td>ROUND OFF</td><td align="right">₹${roundOff.toFixed(2)}</td></tr>` : ""}
            <tr class="bold"><td>SERVICE WALLET COVERED</td><td align="right" style="color:#2563eb;">-₹${serviceWalletDeduction.toFixed(2)}</td></tr>
            <tr class="bold"><td>CASH WALLET USED</td><td align="right" style="color:#dc2626;">-₹${cashWalletUsed.toFixed(2)}</td></tr>
            <tr class="bold large"><td>CUSTOMER PAYABLE</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>
        </table>

        <div class="divider"></div>
        <div class="wallet-box">
            <div class="wallet-box-title">PAYMENT BREAKDOWN</div>
            <table>
                <tr class="bold"><td>Service Wallet</td><td align="right">₹${serviceWalletDeduction.toFixed(2)}</td></tr>
                <tr class="bold"><td>Cash Wallet</td><td align="right">₹${cashWalletUsed.toFixed(2)}</td></tr>
                ${customerPayable > 0 ? `<tr class="bold"><td>Additional Paid</td><td align="right">₹${customerPayable.toFixed(2)}</td></tr>` : ""}
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:4px 0;"></div></td></tr>
                <tr class="bold large"><td>TOTAL PAID</td><td align="right">₹${totalAmountPaid.toFixed(2)}</td></tr>
            </table>
        </div>

        ${serviceWalletEntries.length > 0 ? `
        <div class="divider"></div>
        <div class="wallet-box">
            <div class="wallet-box-title">SERVICE WALLET SUMMARY</div>
            <table>
                ${serviceWalletRowsHtml}
            </table>
        </div>
        ` : ""}

        <div class="divider"></div>
        <div class="wallet-box">
            <div class="wallet-box-title">CASH WALLET SUMMARY</div>
            <table>
                <tr><td>Wallet Name</td><td align="right"><strong>${cashWalletName}</strong></td></tr>
                ${currentCashBalance !== null ? `<tr><td>Previous Wallet Balance</td><td align="right">₹${currentCashBalance.toFixed(2)}</td></tr>` : ""}
                <tr><td>Amount Used (This Bill)</td><td align="right" style="color:#dc2626;"><strong>-₹${cashWalletUsed.toFixed(2)}</strong></td></tr>
                <tr class="bold"><td>Remaining Wallet Balance</td><td align="right" style="color:#16a34a;"><strong>₹${Number(remainingCashWalletBalance ?? 0).toFixed(2)}</strong></td></tr>
                ${cashWalletExpiry ? `<tr><td>Wallet Expiry Date</td><td align="right"><strong>${cashWalletExpiry}</strong></td></tr>` : ""}
            </table>
        </div>

        <div class="divider"></div>

        <div class="bold">GST Tax Summary :</div>
        <table style="margin-top:5px;">
            <thead>
                <tr>
                    <th>GST</th>
                    <th align="right">CGST</th>
                    <th align="right">SGST</th>
                    <th align="right">PCGST</th>
                    <th align="right">PSGST</th>
                </tr>
            </thead>
            <tbody>
                ${gstSummaryRows}
            </tbody>
        </table>

        <div class="divider" style="margin-top:10px;"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
            CONSOLIDATED WALLET REDEMPTION INVOICE.
        </div>
    </div>
</body>
</html>
    `;
};

export const generateInvoiceHTML = (invoice, currentStore) => {
    if (!invoice) return "";
    const billingType = detectBillingType(invoice);

    if (billingType === 'COMBINED_WALLET') {
        return generateCombinedWalletInvoiceHTML(invoice, currentStore);
    } else if (billingType === 'SERVICE_WALLET') {
        return generateServiceWalletInvoiceHTML(invoice, currentStore);
    } else if (billingType === 'CASH_WALLET') {
        return generateCashWalletInvoiceHTML(invoice, currentStore);
    } else {
        return generateNormalInvoiceHTML(invoice, currentStore);
    }
};

/**
 * 4. DEDICATED SERVICE WALLET DETAILS & RECHARGE PRINT LAYOUT
 * Displays total services purchased, extra count, total services, services used, remaining services, validity, expiry, and purchase amount.
 */
export const generateServiceWalletDetailsPrintHTML = (walletOrLog, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(walletOrLog, currentStore);

    const custName = walletOrLog.customerId?.name || walletOrLog.customerId?.customerName || walletOrLog.customerName || "Walk-in";
    const custPhone = walletOrLog.customerId?.phoneNumber || walletOrLog.customerPhone || "-";

    const packageName = walletOrLog.customWalletName ||
        walletOrLog.walletPackageId?.name ||
        walletOrLog.walletName ||
        (walletOrLog.serviceId?.serviceName ? `${walletOrLog.serviceId.serviceName} Package` : "Service Wallet Package");

    const serviceName = walletOrLog.serviceId?.serviceName || walletOrLog.serviceName || "Applicable Service";

    // Calculations:
    // Total Services = Purchased/Allowed Services + Extra Count
    // Remaining Services = Total Services - Services Used
    const extraCount = Number(walletOrLog.additionalCount || walletOrLog.extraCount || 0);
    const purchasedServices = Number(
        walletOrLog.baseCount ||
        walletOrLog.totalServiceCount ||
        walletOrLog.walletPackageId?.totalServiceCount ||
        walletOrLog.totalCount ||
        0
    );
    const totalServices = (purchasedServices > 0 && extraCount > 0)
        ? (purchasedServices + extraCount)
        : Number(walletOrLog.totalCount || purchasedServices || 0);

    const remainingServices = Number(walletOrLog.remainingCount ?? walletOrLog.remainingServiceCount ?? totalServices);
    const servicesUsed = Number(walletOrLog.usedCount || walletOrLog.serviceUsageCount || Math.max(0, totalServices - remainingServices));

    const rechargeDateRaw = walletOrLog.transactionDate || walletOrLog.createdAt || walletOrLog.purchaseDate || walletOrLog.rechargeDate || Date.now();
    const rechargeDateStr = new Date(rechargeDateRaw).toLocaleDateString('en-GB');

    const validityDays = walletOrLog.walletPackageId?.validityDays || walletOrLog.validityDays || walletOrLog.rechargeValidityDays || 365;

    const expiryDateStr = walletOrLog.expiryDate
        ? new Date(walletOrLog.expiryDate).toLocaleDateString('en-GB')
        : (rechargeDateRaw ? new Date(new Date(rechargeDateRaw).getTime() + validityDays * 86400000).toLocaleDateString('en-GB') : "-");

    const isGstApplicable = Boolean(walletOrLog.isGstApplicable || walletOrLog.walletPackageId?.isGstApplicable);
    const gstRate = Number(walletOrLog.gstPercentage ?? walletOrLog.walletPackageId?.gstPercentage ?? 0);
    const gstType = walletOrLog.gstType || walletOrLog.walletPackageId?.gstType || 'Exclusive';

    let totalPayable = Number(
        walletOrLog.amountPaid ??
        walletOrLog.totalAmount ??
        walletOrLog.price ??
        0
    );

    let customerBasePay = Number(
        walletOrLog.customerBasePay ??
        (walletOrLog.price && walletOrLog.price !== totalPayable ? walletOrLog.price : 0)
    );

    let gstAmount = Number(walletOrLog.gstAmount ?? 0);

    if (isGstApplicable && gstRate > 0) {
        if (gstAmount === 0) {
            if (gstType === 'Exclusive') {
                if (customerBasePay === 0) customerBasePay = totalPayable / (1 + gstRate / 100);
                gstAmount = (customerBasePay * gstRate) / 100;
                totalPayable = customerBasePay + gstAmount;
            } else { // Inclusive
                if (customerBasePay === 0 || customerBasePay === totalPayable) {
                    gstAmount = totalPayable - (totalPayable / (1 + gstRate / 100));
                    customerBasePay = totalPayable - gstAmount;
                }
            }
        } else {
            if (gstType === 'Inclusive' && (customerBasePay === 0 || customerBasePay === totalPayable)) {
                customerBasePay = totalPayable - gstAmount;
            } else if (gstType === 'Exclusive' && customerBasePay === 0) {
                customerBasePay = totalPayable - gstAmount;
            }
        }
    } else {
        if (customerBasePay === 0) customerBasePay = totalPayable;
        gstAmount = 0;
    }

    return `
<html>
<head>
    <title>Service Wallet Details - ${packageName}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Wallet Receipt</div>
            <div class="badge-title">SERVICE WALLET DETAILS</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="40%">Customer Name</td><td>: <strong>${custName}</strong></td></tr>
            <tr><td>Phone Number</td><td>: ${custPhone}</td></tr>
            <tr><td>Print Date</td><td>: ${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="wallet-box">
            <div class="wallet-box-title">SERVICE PACKAGE DETAILS</div>
            <table>
                <tr><td>Package Name</td><td align="right"><strong>${packageName}</strong></td></tr>
                <tr><td>Service Name</td><td align="right"><strong>${serviceName}</strong></td></tr>
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr><td>Purchased Services</td><td align="right">${purchasedServices}</td></tr>
                ${extraCount > 0 ? `<tr><td>Extra Count</td><td align="right">+${extraCount} Free</td></tr>` : ""}
                <tr class="bold"><td>Total Services</td><td align="right">${totalServices}</td></tr>
                <tr><td>Services Used</td><td align="right">${servicesUsed}</td></tr>
                <tr class="bold large"><td>Remaining Services</td><td align="right" style="color:#16a34a;">${remainingServices}</td></tr>
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr><td>Recharge Date</td><td align="right">${rechargeDateStr}</td></tr>
                <tr><td>Validity</td><td align="right">${validityDays} Days</td></tr>
                <tr><td>Expiry Date</td><td align="right"><strong>${expiryDateStr}</strong></td></tr>
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr><td>Wallet Base Price</td><td align="right">₹${customerBasePay.toFixed(2)}</td></tr>
                ${isGstApplicable && gstRate > 0 ? `
                <tr><td>GST Rate</td><td align="right">${gstRate}%</td></tr>
                <tr><td>GST Type</td><td align="right">${gstType}</td></tr>
                <tr><td>GST Amount</td><td align="right">₹${gstAmount.toFixed(2)}</td></tr>
                ` : `
                <tr><td>GST Status</td><td align="right">Not Applicable</td></tr>
                `}
                <tr class="bold large"><td>Total Payable / Amount Paid</td><td align="right">₹${totalPayable.toFixed(2)}</td></tr>
                ${walletOrLog.paymentMethod ? `<tr><td>Payment Method</td><td align="right"><strong>${walletOrLog.paymentMethod}</strong></td></tr>` : ""}
            </table>
        </div>

        <div class="divider"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
            SERVICE WALLET SUMMARY RECEIPT.
        </div>
    </div>
</body>
</html>
    `;
};

/**
 * 5. DEDICATED CASH / PRICE WALLET DETAILS & RECHARGE PRINT LAYOUT
 * Displays wallet name, recharge date, Customer Base Pay, GST Rate, GST Type, GST Amount, Total Payable, Wallet Credit Amount, Total Credited Amount, Remaining Balance, Validity, and Expiry dates.
 */
export const generateCashWalletDetailsPrintHTML = (walletOrLog, currentStore) => {
    const { storeName, storeAddress, storePhone, storeGst, storeLogo } = getStoreDetails(walletOrLog, currentStore);

    const custName = walletOrLog.customerId?.name || walletOrLog.customerId?.customerName || walletOrLog.customerName || "Walk-in";
    const custPhone = walletOrLog.customerId?.phoneNumber || walletOrLog.customerPhone || "-";

    const walletName = walletOrLog.customWalletName ||
        walletOrLog.walletName ||
        walletOrLog.walletPackageId?.name ||
        "Cash / Price Wallet";

    const rechargeDateRaw = walletOrLog.transactionDate || walletOrLog.createdAt || walletOrLog.purchaseDate || walletOrLog.rechargeDate || Date.now();
    const rechargeDateStr = new Date(rechargeDateRaw).toLocaleDateString('en-GB');

    const isGstApplicable = Boolean(walletOrLog.isGstApplicable || walletOrLog.walletPackageId?.isGstApplicable);
    const gstRate = Number(walletOrLog.gstPercentage ?? walletOrLog.walletPackageId?.gstPercentage ?? 0);
    const gstType = walletOrLog.gstType || walletOrLog.walletPackageId?.gstType || 'Exclusive';

    let totalPayable = Number(
        walletOrLog.amountPaid ??
        walletOrLog.totalAmount ??
        walletOrLog.price ??
        walletOrLog.rechargeAmount ??
        0
    );

    let customerBasePay = Number(
        walletOrLog.customerBasePay ??
        (walletOrLog.price && walletOrLog.price !== totalPayable ? walletOrLog.price : 0)
    );

    let gstAmount = Number(walletOrLog.gstAmount ?? 0);

    if (isGstApplicable && gstRate > 0) {
        if (gstAmount === 0) {
            if (gstType === 'Exclusive') {
                if (customerBasePay === 0) customerBasePay = totalPayable / (1 + gstRate / 100);
                gstAmount = (customerBasePay * gstRate) / 100;
                totalPayable = customerBasePay + gstAmount;
            } else { // Inclusive
                if (customerBasePay === 0 || customerBasePay === totalPayable) {
                    gstAmount = totalPayable - (totalPayable / (1 + gstRate / 100));
                    customerBasePay = totalPayable - gstAmount;
                }
            }
        } else {
            if (gstType === 'Inclusive' && (customerBasePay === 0 || customerBasePay === totalPayable)) {
                customerBasePay = totalPayable - gstAmount;
            } else if (gstType === 'Exclusive' && customerBasePay === 0) {
                customerBasePay = totalPayable - gstAmount;
            }
        }
    } else {
        if (customerBasePay === 0) customerBasePay = totalPayable;
        gstAmount = 0;
    }

    let walletCreditAmount = Number(
        walletOrLog.walletCredit ??
        walletOrLog.creditedAmount ??
        walletOrLog.walletPackageId?.creditedAmount ??
        0
    );

    const totalCreditedAmount = Number(
        walletOrLog.totalCredited ??
        walletOrLog.balance ??
        walletCreditAmount ??
        0
    );

    if (walletCreditAmount === 0 && totalCreditedAmount > 0) {
        walletCreditAmount = totalCreditedAmount;
    }

    const remainingBalance = Number(walletOrLog.balance ?? walletOrLog.remainingWalletBalance ?? 0);
    const amountUsed = Number(
        walletOrLog.usedAmount ||
        walletOrLog.walletAmountUsed ||
        Math.max(0, totalCreditedAmount - remainingBalance)
    );

    const walletValidityDays = walletOrLog.walletPackageId?.validityDays || walletOrLog.validityDays || 365;

    const walletExpiryDateStr = walletOrLog.expiryDate
        ? new Date(walletOrLog.expiryDate).toLocaleDateString('en-GB')
        : (rechargeDateRaw ? new Date(new Date(rechargeDateRaw).getTime() + walletValidityDays * 86400000).toLocaleDateString('en-GB') : "-");

    return `
<html>
<head>
    <title>Cash Wallet Details - ${walletName}</title>
    <style>${getThermalCss()}</style>
</head>
<body>
    <div class="box">
        <div class="center">
            ${storeLogo ? `<div class="logo-container"><img src="${storeLogo}" class="logo" alt="Store Logo" /></div>` : ""}
            <div class="store-name">${storeName}</div>
            <div class="tax-title">Wallet Receipt</div>
            <div class="badge-title">CASH / PRICE WALLET DETAILS</div>
            <div style="margin-top:5px;">${storeAddress}</div>
            <div>Ph:- ${storePhone}</div>
            ${storeGst ? `<div>GST No: ${storeGst}</div>` : ""}
        </div>

        <div class="divider"></div>

        <table>
            <tr><td width="40%">Customer Name</td><td>: <strong>${custName}</strong></td></tr>
            <tr><td>Phone Number</td><td>: ${custPhone}</td></tr>
            <tr><td>Print Date</td><td>: ${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</td></tr>
        </table>

        <div class="divider"></div>

        <div class="wallet-box">
            <div class="wallet-box-title">CASH WALLET BREAKDOWN</div>
            <table>
                <tr><td>Wallet Name</td><td align="right"><strong>${walletName}</strong></td></tr>
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr><td>Recharge / Transaction Date</td><td align="right">${rechargeDateStr}</td></tr>
                <tr><td>Customer Base Pay</td><td align="right">₹${customerBasePay.toFixed(2)}</td></tr>
                ${isGstApplicable && gstRate > 0 ? `
                <tr><td>GST Rate</td><td align="right">${gstRate}%</td></tr>
                <tr><td>GST Type</td><td align="right">${gstType}</td></tr>
                <tr><td>GST Amount</td><td align="right">₹${gstAmount.toFixed(2)}</td></tr>
                ` : `
                <tr><td>GST Status</td><td align="right">Not Applicable</td></tr>
                `}
                <tr class="bold large"><td>Total Payable / Amount Paid</td><td align="right">₹${totalPayable.toFixed(2)}</td></tr>
                ${walletOrLog.paymentMethod ? `<tr><td>Payment Method</td><td align="right"><strong>${walletOrLog.paymentMethod}</strong></td></tr>` : ""}
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr class="bold"><td>Wallet Credit Amount</td><td align="right" style="color:#b91c1c;">₹${walletCreditAmount.toFixed(2)}</td></tr>
                <tr class="bold"><td>Total Credited Amount</td><td align="right">₹${totalCreditedAmount.toFixed(2)}</td></tr>
                ${amountUsed > 0 ? `<tr><td>Amount Used</td><td align="right">₹${amountUsed.toFixed(2)}</td></tr>` : ""}
                <tr class="bold large"><td>Remaining Wallet Balance</td><td align="right" style="color:#16a34a;">₹${remainingBalance.toFixed(2)}</td></tr>
                <tr><td colspan="2"><div style="border-top:1px dotted #ccc; margin:3px 0;"></div></td></tr>
                <tr><td>Wallet Validity</td><td align="right">${walletValidityDays} Days</td></tr>
                <tr><td>Wallet Expiry Date</td><td align="right"><strong>${walletExpiryDateStr}</strong></td></tr>
            </table>
        </div>

        <div class="divider"></div>

        <div class="footer">
            THANK YOU. HAVE A NICE DAY.<br/>
            CASH WALLET SUMMARY RECEIPT.
        </div>
    </div>
</body>
</html>
    `;
};

/**
 * MASTER WALLET PRINT ROUTER
 * Auto detects Service Wallet vs Cash Wallet and renders the corresponding detailed layout.
 */
export const generateWalletPrintHTML = (walletOrLog, currentStore) => {
    if (!walletOrLog) return "";
    const type = walletOrLog.type || walletOrLog.walletType;
    if (type === 'Service') {
        return generateServiceWalletDetailsPrintHTML(walletOrLog, currentStore);
    } else {
        return generateCashWalletDetailsPrintHTML(walletOrLog, currentStore);
    }
};

